export default {
    props: {
        allSessions: {
            type: Object,
            default: {}
        },
        activeSession: [String, Number],
        hasMore: {
            type: Boolean,
            default: false
        },
        totalCount: {
            type: Number,
            default: 0
        },
        syncEnabled: {
            type: Boolean,
            default: false
        },
        isLoading: {
            type: Boolean,
            default: false
        },
        usingLocalFallback: {
            type: Boolean,
            default: false
        },
        loadErrorMessage: {
            type: String,
            default: ''
        },
        exportingSessionKey: {
            type: String,
            default: null
        },
        exportProgressMessage: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            sideBarActive: false,
            searchQuery: '', // Added for search input
            debouncedQuery: '', // For storing the debounced query
            actionMenuKey: null,
            actionMenuStyle: {},
            renameSessionKey: null,
            renameValue: '',
            suppressClickKey: null,
        }
    },
    computed: {
        displayedCount() {
            // Prefer server-provided total when available
            if (typeof this.totalCount === 'number' && this.totalCount > 0) {
                return this.totalCount;
            }
            return Object.entries(this.filteredSessions).length;
        },
        filteredSessions() {
            if (!this.debouncedQuery) {
                return this.allSessions;
            }

            // Normalize search query: remove diacritics, convert to lowercase, and replace multiple spaces with a single space
            const normalizedQuery = this.normalizeText(this.debouncedQuery);

            console.log('Normalized query for chat search:', normalizedQuery);

            return Object.keys(this.allSessions).filter(sessionKey => {
                const session = this.allSessions[sessionKey];

                // Normalize the session title
                const normalizedTitle = this.normalizeText(session.title);

                // Check if the normalized search query matches the normalized title
                const matchesTitle = normalizedTitle.includes(normalizedQuery);

                if (matchesTitle) {
                    return true;
                }

                // Check if the normalized search query matches any normalized message content
                const matchesContent = session.data.some(message =>
                    (message.text && this.normalizeText(message.text).includes(normalizedQuery)) || // For user/bot messages
                    (message.content && this.normalizeText(message.content).includes(normalizedQuery)) // For system messages
                );

                return matchesTitle || matchesContent;
            }).reduce((result, key) => {
                result[key] = this.allSessions[key];
                return result;
            }, {});
        },
        sortedSessionKeys() {
            const sessionsArray = Object.entries(this.filteredSessions);
            // Sort by created_at in descending order (newest first)
            // Fallback to 0 for sessions that might somehow miss a created_at timestamp
            sessionsArray.sort(([, sessionA], [, sessionB]) => {
                const pinnedA = sessionA && (sessionA.is_pinned === true || sessionA.is_pinned === 1 || sessionA.is_pinned === '1');
                const pinnedB = sessionB && (sessionB.is_pinned === true || sessionB.is_pinned === 1 || sessionB.is_pinned === '1');
                if (pinnedA !== pinnedB) {
                    return pinnedA ? -1 : 1;
                }
                return (sessionB.created_at || 0) - (sessionA.created_at || 0);
            });
            return sessionsArray.map(([key]) => key);
        },
        hasCollaborativeUnread() {
            return Object.keys(this.allSessions || {}).some((sessionKey) => (
                this.isCollaborativeSession(sessionKey) && this.sessionUnreadCount(sessionKey) > 0
            ));
        },
        statusBanner() {
            if (!this.syncEnabled) return null;
            if (this.isLoading) {
                return {
                    variant: 'info',
                    title: 'Sincronizando tus chats...',
                    message: 'Mostramos los chats guardados en este dispositivo mientras obtenemos los del servidor.',
                    icon: 'anticon anticon-sync anticon-spin',
                    showRetry: false
                };
            }
            if (this.usingLocalFallback) {
                return {
                    variant: 'warning',
                    title: 'Mostrando chats del dispositivo',
                    message: this.loadErrorMessage || 'No pudimos conectar con el servidor. Tus chats locales siguen disponibles.',
                    icon: 'anticon anticon-warning',
                    showRetry: true
                };
            }
            return null;
        }
    },
    methods: {
        dateFormat(date) {
            return moment(date).format('D MMM, Y')
        },
        deleteSession(sessionKey) {
            if (!this.canManageSession(sessionKey)) {
                this.closeActionMenu();
                return;
            }
            if (confirm('¿Desea eliminar el chat?')) {
                this.$emit('deleteSession', sessionKey);
            }
            this.closeActionMenu();
        },
        leaveSession(sessionKey) {
            if (!this.canLeaveSession(sessionKey)) {
                this.closeActionMenu();
                return;
            }
            if (confirm('¿Desea abandonar este chat colaborativo? Ya no aparecerá en su lista de chats.')) {
                this.$emit('leaveSession', sessionKey);
            }
            this.closeActionMenu();
        },
        handleDocumentClick(event) {
            if (!this.actionMenuKey) return;
            const target = event && event.target;
            if (!target || typeof target.closest !== 'function') {
                this.closeActionMenu();
                return;
            }
            if (target.closest('.chat-actions-menu') || target.closest('.chat-actions-trigger') || target.closest('.chat-rename-dialog')) {
                return;
            }
            this.closeActionMenu();
        },
        openActionMenu(sessionKey) {
            this.actionMenuKey = sessionKey;
            this.actionMenuStyle = {};
            this.$nextTick(this.positionActionMenu);
        },
        closeActionMenu() {
            this.actionMenuKey = null;
            this.actionMenuStyle = {};
        },
        positionActionMenu() {
            const menuRef = this.$refs.actionMenu;
            const menu = Array.isArray(menuRef) ? menuRef[0] : menuRef;
            const scrollContainer = this.$refs.sessionsScroll;
            if (!menu || !scrollContainer) return;

            const row = menu.closest('.chat-session-row');
            if (!row) return;

            const menuRect = menu.getBoundingClientRect();
            const scrollRect = scrollContainer.getBoundingClientRect();
            const rowRect = row.getBoundingClientRect();
            const gap = 8;
            const spaceBelow = scrollRect.bottom - menuRect.top;
            const spaceAbove = rowRect.top - scrollRect.top - gap;
            const placeAbove = menuRect.bottom > scrollRect.bottom && spaceAbove > spaceBelow;
            const availableSpace = Math.max(placeAbove ? spaceAbove : spaceBelow, 0);

            this.actionMenuStyle = {
                maxHeight: `${availableSpace}px`,
                overflowY: 'auto',
                top: placeAbove ? 'auto' : null,
                bottom: placeAbove ? `calc(100% + ${gap}px)` : null,
                marginTop: placeAbove ? '0' : null
            };
        },
        toggleActionMenu(sessionKey) {
            if (this.actionMenuKey === sessionKey) {
                this.closeActionMenu();
                return;
            }
            this.openActionMenu(sessionKey);
        },
        handleSessionClick(sessionKey, event) {
            if (this.suppressClickKey === sessionKey) {
                return;
            }
            this.closeActionMenu();
            this.$emit('sessionSelected', sessionKey);
        },
        startRename(sessionKey) {
            if (!this.canManageSession(sessionKey)) {
                this.closeActionMenu();
                return;
            }
            const session = this.filteredSessions[sessionKey] || {};
            const currentTitle = typeof session.title === 'string' ? session.title : '';
            this.renameSessionKey = sessionKey;
            this.renameValue = currentTitle;
            this.closeActionMenu();
        },
        confirmRename() {
            const sessionKey = this.renameSessionKey;
            const value = typeof this.renameValue === 'string' ? this.renameValue.trim() : '';
            if (!sessionKey) return;
            if (!value) {
                alert('El nombre no puede estar vacío.');
                return;
            }
            this.$emit('renameSession', sessionKey, value);
            this.renameSessionKey = null;
            this.renameValue = '';
        },
        cancelRename() {
            this.renameSessionKey = null;
            this.renameValue = '';
        },
        togglePin(sessionKey) {
            if (!this.canManageSession(sessionKey)) {
                this.closeActionMenu();
                return;
            }
            const session = this.filteredSessions[sessionKey] || {};
            const isPinned = session && (session.is_pinned === true || session.is_pinned === 1 || session.is_pinned === '1');
            this.$emit('pinSession', sessionKey, !isPinned);
            this.closeActionMenu();
        },
        exportSession(sessionKey) {
            if (!sessionKey || this.exportingSessionKey) return;
            this.$emit('exportSession', sessionKey);
            this.closeActionMenu();
        },
        isPinned(sessionKey) {
            const session = this.filteredSessions[sessionKey] || {};
            return session && (session.is_pinned === true || session.is_pinned === 1 || session.is_pinned === '1');
        },
        isCollaborativeSession(sessionKey) {
            const session = this.filteredSessions[sessionKey] || this.allSessions[sessionKey] || {};
            const value = session.is_collaborative;
            return value === true || value === 1 || value === '1' || value === 'true';
        },
        sessionUnreadCount(sessionKey) {
            const session = this.filteredSessions[sessionKey] || this.allSessions[sessionKey] || {};
            const count = Number(session.unread_count || 0);
            if (!Number.isFinite(count) || count <= 0) return 0;
            return Math.floor(count);
        },
        formatUnreadCount(sessionKey) {
            const count = this.sessionUnreadCount(sessionKey);
            return count > 99 ? '99+' : String(count);
        },
        canManageSession(sessionKey) {
            const session = this.filteredSessions[sessionKey] || this.allSessions[sessionKey] || {};
            return !(session.is_collaborative && session.collaboration_role === 'member');
        },
        canLeaveSession(sessionKey) {
            const session = this.filteredSessions[sessionKey] || this.allSessions[sessionKey] || {};
            return !!(session.is_collaborative && session.collaboration_role === 'member');
        },
        isExporting(sessionKey) {
            return !!sessionKey && this.exportingSessionKey === sessionKey;
        },
        // Add this method to check if the click is inside the search input
        isClickInsideSearch(event) {
            return event.target.closest('input[type="text"]') !== null;
        },
        handleSidebarClick(event) {
            // Only close the sidebar if the click is outside the search box
            if (!this.isClickInsideSearch(event)) {
                this.sideBarActive = false;
            }
            this.closeActionMenu();
        },
        // Normalize text by removing diacritics, converting to lowercase, and replacing multiple spaces with a single space
        normalizeText(text) {
            return text
                .normalize("NFD") // Normalize to decomposed form
                .replace(/[\u0300-\u036f]/g, "") // Remove diacritics
                .toLowerCase() // Convert to lowercase
                .replace(/\s+/g, " ") // Replace multiple spaces with a single space
                .trim(); // Remove leading and trailing spaces
        },
        debounce(func, delay) {
            let timeout;
            return function (...args) {
                clearTimeout(timeout);
                timeout = setTimeout(() => func.apply(this, args), delay);
            };
        },
        updateDebouncedQuery: function () {
            this.debouncedQuery = this.searchQuery;
            // When sync is enabled, ask parent to run server-side search
            try {
                if (window.sync_enabled === true) {
                    this.$emit('searchQueryChanged', this.debouncedQuery || '');
                }
            } catch (e) { }
        },
        handleRetryClick() {
            this.$emit('retryLoad');
        },
        scrollSessionIntoView(sessionKey = this.activeSession) {
            if (!sessionKey) return;
            this.$nextTick(() => {
                const scrollContainer = this.$refs.sessionsScroll;
                if (!scrollContainer) return;

                const targetKey = String(sessionKey);
                const rows = Array.from(scrollContainer.querySelectorAll('.chat-session-row'));
                const row = rows.find((candidate) => candidate.getAttribute('data-session-key') === targetKey);
                if (!row) return;

                const containerRect = scrollContainer.getBoundingClientRect();
                const rowRect = row.getBoundingClientRect();
                const viewportHeight = window.innerHeight || document.documentElement.clientHeight || containerRect.bottom;
                const visibleTop = Math.max(containerRect.top, 0);
                const visibleBottom = Math.min(containerRect.bottom, viewportHeight);
                const padding = 8;

                if (rowRect.top < visibleTop + padding) {
                    const nextTop = scrollContainer.scrollTop + (rowRect.top - visibleTop) - padding;
                    const clampedTop = Math.max(nextTop, 0);
                    scrollContainer.scrollTop = clampedTop;
                    if (typeof scrollContainer.scrollTo === 'function') {
                        scrollContainer.scrollTo({ top: clampedTop, behavior: 'auto' });
                    }
                    return;
                }

                if (rowRect.bottom > visibleBottom - padding) {
                    const nextTop = scrollContainer.scrollTop + (rowRect.bottom - visibleBottom) + padding;
                    const clampedTop = Math.max(nextTop, 0);
                    scrollContainer.scrollTop = clampedTop;
                    if (typeof scrollContainer.scrollTo === 'function') {
                        scrollContainer.scrollTo({ top: clampedTop, behavior: 'auto' });
                    }
                }
            });
        }
    },
    watch: {
        activeSession() {
            this.scrollSessionIntoView();
        },
        sortedSessionKeys() {
            this.scrollSessionIntoView();
        }
    },
    created() {
        // Initialize the debounced method
        this.debouncedUpdateQuery = this.debounce(this.updateDebouncedQuery, 300);
    },
    mounted() {
        document.addEventListener('click', this.handleDocumentClick);
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleDocumentClick);
    },
    /*html*/
    template: `
        <button 
            type="button"
            class="chat-sidebar-toggle absolute !top-4 left-4"
            :class="{ 'is-active': sideBarActive, 'has-unread': hasCollaborativeUnread }"
            :aria-label="sideBarActive ? 'Cerrar chats' : 'Abrir chats'"
            :title="sideBarActive ? 'Cerrar chats' : 'Abrir chats'"
            :aria-expanded="sideBarActive"
            aria-controls="chat-history-panel"
            data-testid="chat-sidebar-toggle"
            @click="sideBarActive = !sideBarActive"
        >
            <svg xmlns="http://www.w3.org/2000/svg" class="fill-theme-color" width="24.75" height="20.5" viewBox="0 0 24.75 20.5">
                <g id="hamburger-menu" transform="translate(-5.625 -7.75)">
                    <path id="Path_581" data-name="Path 581" d="M5.625,6.75h24.75V9H5.625Z" transform="translate(0 1)"/>
                    <path id="Path_582" data-name="Path 582" d="M5.625,16.875h24.75v2.25H5.625Z"/>
                    <path id="Path_583" data-name="Path 583" d="M5.625,27h24.75v2.25H5.625Z" transform="translate(0 -1)"/>
                </g>
            </svg>
            <span
                v-if="hasCollaborativeUnread"
                class="chat-sidebar-toggle__unread-dot"
                aria-hidden="true"
            ></span>
        </button>
        
        <div 
            id="chat-history-panel"
            class="chat-sidebar-panel h-full absolute lg:static bg-primary z-10 left-0 transition-all duration-500"
            :class="{ 'is-open': sideBarActive, 'is-closed': !sideBarActive }"
            data-testid="chat-sidebar-panel"
            style="height: 100%;"
            @click="handleSidebarClick"
        >
            <div 
                class="!p-3 w-[280px] shadow-md h-full flex flex-col min-h-0"
                style="height: 100%; display: flex; flex-direction: column; min-height: 0;"
                data-tour="chat-history"
                @click="sideBarActive = false"
            >
                <div class="shrink-0" style="flex: 0 0 auto;">

                    <div class="chat-sidebar-header flex items-center pb-7 !pt-3 justify-between">
                        <div class="chat-sidebar-title-group flex gap-2 items-center">
                            <h3 
                                class="fw-bold text-lg text-secondary"
                            >
                                Chats
                            </h3>
                            <span 
                                class="min-w-[30px] h-[25px] !px-2 flex justify-center items-center font-medium bg-theme-color text-primary rounded-full"
                                v-if="displayedCount > 0"
                            >
                                {{ displayedCount }}
                            </span>
                        </div>
                        
                        <button 
                            v-if="sideBarActive"
                            class="chat-sidebar-close border-fourth border rounded-md text-sm w-8 h-8 flex justify-center items-center text-secondary hover:text-primary hover:bg-theme-color transition-all" 
                            aria-label="Cerrar chats"
                            title="Cerrar chats"
                            @click="sideBarActive = !sideBarActive"
                        >
                            <i class="anticon anticon-close" ></i>
                        </button>
                    </div>

                    <input
                        type="text"
                        v-model="searchQuery"
                        @input="debouncedUpdateQuery" // Call the debounced update method
                        placeholder="Buscar chats..."
                        class="w-full mb-4 p-2 border rounded dynamic-color"
                        @click.stop
                    />

                    <div
                        v-if="statusBanner"
                        class="alert text-sm mb-3"
                        :class="statusBanner.variant === 'warning' ? 'alert-warning' : 'alert-info'"
                    >
                        <div class="flex gap-2">
                            <i :class="statusBanner.icon"></i>
                            <div class="flex-1">
                                <p class="font-semibold mb-1">{{ statusBanner.title }}</p>
                                <p class="mb-0">{{ statusBanner.message }}</p>
                            </div>
                        </div>
                        <div class="mt-2" v-if="statusBanner.showRetry">
                            <button
                                class="btn btn-sm btn-link px-0"
                                type="button"
                                @click.stop="handleRetryClick"
                            >
                            Reintentar
                            </button>
                        </div>
                    </div>

                    <div
                        v-if="exportingSessionKey"
                        class="alert alert-info text-sm mb-3 export-progress-banner"
                    >
                        <div class="flex gap-2 items-start">
                            <i class="anticon anticon-loading anticon-spin mt-1"></i>
                            <div class="flex-1">
                                <p class="font-semibold mb-1">Generando PDF…</p>
                                <p class="mb-0">{{ exportProgressMessage || 'Estamos preparando el historial completo del chat para su descarga.' }}</p>
                            </div>
                        </div>
                    </div>

                    <button 
                        class="!p-3 mb-2 border border-fourth rounded-lg block w-full font-medium hover:bg-theme-color hover:border-theme-color transition-colors hover:text-primary text-theme-color"
                        data-tour="chat-call"
                        @click="$emit('startAudioCall')"
                    >
                        <i class="anticon anticon-phone"></i>
                        Iniciar llamada
                    </button>

                    <button 
                        class="!p-3 mb-3 border border-fourth rounded-lg block w-full font-medium hover:bg-theme-color hover:border-theme-color transition-colors hover:text-primary text-theme-color"
                        data-tour="chat-new"
                        @click="$emit('newSessionSelected')"
                    >
                        <i class="anticon anticon-plus"></i>
                        Nuevo Chat
                    </button>
                </div>

                <div 
                    ref="sessionsScroll"
                    data-testid="chat-sessions-scroll"
                    class="flex-1 min-h-0 w-full overflow-y-auto"
                    style="flex: 1 1 auto; min-height: 0; max-height: calc(100vh - 260px); overflow-y: auto;"
                    @click="sideBarActive = false"
                >
                    <transition-group
                        tag="div"
                        enter-active-class="transition-all"
                        leave-active-class="transition-all"
                        enter-from-class="opacity-0 translate-x-[30px]"
                        leave-to-class="opacity-0 translate-x-[30px]"
                    >
                        <div 
                            class="chat-session-row text-secondary w-full text-left !p-4 mb-3 !py-2 rounded-lg hover:bg-fourth transition-colors flex justify-between group cursor-pointer relative border border-transparent"
                            @click="handleSessionClick(sessionKey, $event)"
                            @contextmenu.prevent
                            :class="{'bg-fourth': activeSession == sessionKey, 'border-theme-color border-opacity-30': actionMenuKey === sessionKey}"
                            v-for="sessionKey in sortedSessionKeys"
                            :key="sessionKey"
                            :data-session-key="sessionKey"
                            :aria-current="activeSession == sessionKey ? 'true' : null"
                            data-bs-toggle="tooltip"
                            data-placement="top"
                            :title="(filteredSessions[sessionKey].page_title ? 'Chat acerca de: ' + filteredSessions[sessionKey].page_title : '') + (filteredSessions[sessionKey].restored_from_backup ? ' Este chat fue restaurado desde una copia de seguridad, asegúrate de usarlo en un único dispositivo' : '')"
                        >
                            <div class="overflow-hidden min-w-0 flex-1">
                                <div class="flex items-center gap-2">
                                    <span
                                        class="font-bold block whitespace-nowrap text-ellipsis overflow-hidden flex-1 min-w-0"
                                    >
                                        {{ filteredSessions[sessionKey].title }}
                                    </span>
                                    <i
                                        v-if="isCollaborativeSession(sessionKey)"
                                        class="chat-collaboration-indicator anticon anticon-team"
                                        title="Chat colaborativo"
                                        aria-label="Chat colaborativo"
                                    ></i>
                                    <span
                                        v-if="isCollaborativeSession(sessionKey) && sessionUnreadCount(sessionKey) > 0"
                                        class="chat-session-unread-badge"
                                        :aria-label="sessionUnreadCount(sessionKey) === 1 ? '1 mensaje no leído' : sessionUnreadCount(sessionKey) + ' mensajes no leídos'"
                                        data-testid="chat-session-unread-badge"
                                    >
                                        {{ formatUnreadCount(sessionKey) }}
                                    </span>
                                    <i v-if="isPinned(sessionKey)" class="anticon anticon-pushpin text-theme-color"></i>
                                </div>
                                <span> {{ dateFormat(filteredSessions[sessionKey].created_at) }} </span>
                                <i v-if="filteredSessions[sessionKey].is_multi_document" class="anticon anticon-folder"></i>
                                <i v-else-if="filteredSessions[sessionKey].is_call_log" class="anticon anticon-phone"></i>
                                <i v-else-if="filteredSessions[sessionKey].path || filteredSessions[sessionKey].is_user_document || filteredSessions[sessionKey].is_document_chat" class="anticon anticon-file"></i>

                                <i v-if="filteredSessions[sessionKey].restored_from_backup" class="anticon anticon-cloud-download"></i>
                            </div>
                            <div class="flex items-center shrink-0 pl-3">
                                <button 
                                    class="chat-actions-trigger transition-opacity hover:text-theme-color rounded-md w-8 h-8 flex items-center justify-center"
                                    :class="{'!opacity-100': activeSession == sessionKey || actionMenuKey === sessionKey}"
                                    @click.stop="toggleActionMenu(sessionKey)"
                                    aria-label="Opciones del chat"
                                    type="button"
                                >
                                    <i class="anticon anticon-menu"></i>
                                </button>
                            </div>
                            <div 
                                v-if="actionMenuKey === sessionKey"
                                class="chat-actions-menu absolute left-3 right-3 top-full mt-2 sm:left-auto sm:right-3 sm:top-3 sm:mt-0 sm:w-48 rounded-xl border border-fourth bg-primary shadow-lg z-20 overflow-hidden"
                                @click.stop
                                ref="actionMenu"
                                :style="actionMenuStyle"
                            >
                                <button
                                    v-if="canManageSession(sessionKey)"
                                    class="w-full px-3 py-2 text-sm flex items-center gap-2 text-secondary hover:bg-fourth transition-colors"
                                    @click.stop="togglePin(sessionKey)"
                                    type="button"
                                >
                                    <i class="anticon anticon-pushpin"></i>
                                    <span>{{ isPinned(sessionKey) ? 'Quitar fijado' : 'Fijar' }}</span>
                                </button>
                                <button
                                    v-if="canManageSession(sessionKey)"
                                    class="w-full px-3 py-2 text-sm flex items-center gap-2 text-secondary hover:bg-fourth transition-colors"
                                    @click.stop="startRename(sessionKey)"
                                    type="button"
                                >
                                    <i class="anticon anticon-edit"></i>
                                    <span>Cambiar nombre</span>
                                </button>
                                <button
                                    class="w-full px-3 py-2 text-sm flex items-center gap-2 text-secondary hover:bg-fourth transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                    @click.stop="exportSession(sessionKey)"
                                    :disabled="!!exportingSessionKey"
                                    type="button"
                                >
                                    <i :class="isExporting(sessionKey) ? 'anticon anticon-loading anticon-spin' : 'anticon anticon-file-pdf'"></i>
                                    <span>{{ isExporting(sessionKey) ? 'Generando PDF...' : 'Descargar PDF' }}</span>
                                </button>
                                <button
                                    v-if="canManageSession(sessionKey)"
                                    class="chat-actions-delete w-full px-3 py-2 text-sm flex items-center gap-2 transition-colors"
                                    @click.stop="deleteSession(sessionKey)"
                                    type="button"
                                >
                                    <i class="anticon anticon-delete"></i>
                                    <span>Borrar</span>
                                </button>
                                <button
                                    v-if="canLeaveSession(sessionKey)"
                                    class="chat-actions-delete w-full px-3 py-2 text-sm flex items-center gap-2 transition-colors"
                                    @click.stop="leaveSession(sessionKey)"
                                    type="button"
                                >
                                    <i class="anticon anticon-logout"></i>
                                    <span>Abandonar</span>
                                </button>
                            </div>
                        </div>
                    </transition-group>
                    <!-- Load more for server-backed pagination -->
                    <div v-if="hasMore" class="px-4 pb-4">
                        <button 
                          class="!p-2 border border-fourth rounded-lg w-full font-medium hover:bg-theme-color hover:border-theme-color transition-colors hover:text-primary text-theme-color"
                          @click.stop="$emit('loadMore')"
                        >
                          Cargar más
                        </button>
                    </div>
                </div>
                <div 
                    v-if="renameSessionKey"
                    class="chat-rename-backdrop fixed inset-0 z-30 flex items-center justify-center bg-black/40 px-4"
                    @click.self="cancelRename"
                >
                    <div class="chat-rename-dialog bg-primary rounded-2xl shadow-lg w-full max-w-sm p-4 border border-fourth" @click.stop>
                        <div class="flex items-center justify-between mb-3">
                            <h5 class="m-0 text-secondary font-semibold">Cambiar nombre</h5>
                            <button class="btn btn-link p-0 text-secondary" @click="cancelRename" type="button">
                                <i class="anticon anticon-close"></i>
                            </button>
                        </div>
                        <input
                            v-model="renameValue"
                            type="text"
                            maxlength="200"
                            class="form-control"
                            placeholder="Nuevo nombre del chat"
                            @keydown.enter.prevent="confirmRename"
                        />
                        <div class="text-muted small mt-2">Máximo 200 caracteres.</div>
                        <div class="mt-4 flex justify-end gap-2">
                            <button class="btn btn-outline-secondary" type="button" @click="cancelRename">Cancelar</button>
                            <button class="btn btn-primary chat-rename-confirm-button" type="button" @click="confirmRename">Guardar</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    `
}
